/**
 * Skeleton — house loading placeholders (spec 121 item 12).
 *
 * A tiny set of pulse-shimmer primitives used in place of bare `Loader2`
 * spinners / "Loading…" text on the high-traffic data-heavy lists. The
 * base block uses the house pulse style (`animate-pulse rounded
 * bg-[var(--hub-color-surface-sunken)]`) and respects
 * `prefers-reduced-motion` because Tailwind's `animate-pulse` is already
 * gated by the global reduced-motion reset. `animate-pulse` stays a literal
 * Tailwind class name (spec 230 Global Constraint 6) — it is not restyled
 * away, only the fill colour is moved onto a `--hub-*` token.
 *
 * Visual only — these components render no data and carry no behavior. They
 * are intentionally dumb so a page can drop them in while its real data
 * resolves and swap them out once `loading` flips false.
 */

interface SkeletonProps {
  /** Extra classes composed after the base pulse style (width/height/shape). */
  className?: string;
}

/** Single shimmer block. Compose `className` for size/shape. */
export function Skeleton({ className = '' }: SkeletonProps) {
  return (
    <div
      aria-hidden
      className={`animate-pulse rounded-md bg-[var(--hub-color-surface-sunken)] ${className}`}
    />
  );
}

interface SkeletonTextProps {
  /** Tailwind width class for the line (default `w-full`). */
  width?: string;
  className?: string;
}

/** A single text-height shimmer line. */
export function SkeletonText({ width = 'w-full', className = '' }: SkeletonTextProps) {
  return <Skeleton className={`h-4 ${width} ${className}`} />;
}

interface SkeletonRowsProps {
  /** Number of shimmer rows (default 6). */
  rows?: number;
  /** Number of cells per row (default 4). */
  cols?: number;
}

/**
 * A block of evenly-spaced shimmer rows, each with `cols` cells of varying
 * width to read as a list/table body rather than a flat grey bar.
 */
export function SkeletonRows({ rows = 6, cols = 4 }: SkeletonRowsProps) {
  return (
    <div className="space-y-3" aria-hidden data-testid="skeleton-rows">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex items-center gap-4">
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton
              key={c}
              className={`h-4 ${c === 0 ? 'w-1/3' : 'flex-1'}`}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

interface TableSkeletonProps {
  /** Body rows (default 8). */
  rows?: number;
  /** Columns including any header cells (default 5). */
  cols?: number;
  /** Optional testid passed through so existing loading-state gates keep matching. */
  'data-testid'?: string;
}

/**
 * A carded table placeholder: a header bar plus N body rows inside the house
 * `rounded-lg border bg-white` table shell. Drop-in for a table's loading
 * state.
 */
export function TableSkeleton({
  rows = 8,
  cols = 5,
  'data-testid': testId,
}: TableSkeletonProps) {
  return (
    <div
      className="overflow-hidden rounded-lg border border-slate-200 bg-white"
      data-testid={testId}
    >
      {/* Header bar */}
      <div className="flex items-center gap-4 border-b border-slate-100 bg-slate-50 px-4 py-3">
        {Array.from({ length: cols }).map((_, c) => (
          <Skeleton
            key={c}
            className={`h-3 ${c === 0 ? 'w-1/4' : 'flex-1'}`}
          />
        ))}
      </div>
      {/* Body rows */}
      <div className="divide-y divide-slate-100">
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} className="flex items-center gap-4 px-4 py-3">
            {Array.from({ length: cols }).map((_, c) => (
              <Skeleton
                key={c}
                className={`h-4 ${c === 0 ? 'w-1/4' : 'flex-1'}`}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

interface CardListSkeletonProps {
  /** Number of card placeholders (default 5). */
  cards?: number;
  'data-testid'?: string;
}

/**
 * A stack of card placeholders for card-list pages (e.g. Responses,
 * Playbooks). Each card shows a title bar + two body lines + a meta row.
 */
export function CardListSkeleton({
  cards = 5,
  'data-testid': testId,
}: CardListSkeletonProps) {
  return (
    <div className="space-y-3" data-testid={testId}>
      {Array.from({ length: cards }).map((_, i) => (
        <div
          key={i}
          className="rounded-lg border border-slate-200 bg-white p-4"
        >
          <div className="flex items-center justify-between">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-5 w-16 rounded-full" />
          </div>
          <Skeleton className="mt-3 h-3 w-full" />
          <Skeleton className="mt-2 h-3 w-4/5" />
          <div className="mt-3 flex items-center gap-3">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
      ))}
    </div>
  );
}
